import React from 'react';
import { ArrowRightIcon, CompassIcon, FlagIcon, MousePointerClickIcon, RefreshCwIcon, UsersIcon } from 'lucide-react';
import { Slide } from '../Slide';
import { InView } from '../InView';
import { SlideHeadline } from '../SlideHeadline';
import { chapters } from '../../../data/journey';
import { measures, signalLoop } from '../../../data/deck';
import type { MeasureId } from '../../../data/deck';
const GLYPHS: Record<MeasureId, React.ComponentType<{
  className?: string;
}>> = {
  archetype: UsersIcon,
  prior: CompassIcon,
  progress: FlagIcon,
  nextStep: MousePointerClickIcon,
  sources: RefreshCwIcon
};
interface SignalsSlideProps {
  onOpenSignals: () => void;
}
export function SignalsSlide({
  onOpenSignals
}: SignalsSlideProps) {
  return <Slide id="signals" data-id="element-224" data-mp="323dbva856ghv">
      <div className="grid items-center gap-14 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-16" data-id="element-225" data-mp="3pur83r9eyw84">
        <div data-id="element-226" data-mp="3ul7lxch5cjv0">
          <InView data-id="element-227" data-mp="3ulr3gs2vx68i">
            <SlideHeadline lead="More engagement." follow="More insight." data-id="element-228" data-mp="3um992fbfswt3" />
          </InView>
          <InView delay={0.06} data-id="element-229" data-mp="3hydvykxjcukx">
            <p className="mt-8 max-w-[38ch] font-display text-[19px] font-medium leading-[1.35] text-navy/60" data-id="element-230" data-mp="3hv86v5owpsp7">
              The concept doesn’t just communicate. It helps us understand how specialists prefer to engage — and how
              their thinking moves.
            </p>
          </InView>
          <InView delay={0.12} className="mt-12" data-id="element-231" data-mp="3mucevt03hkdy">
            <div className="flex flex-wrap items-center gap-2" data-id="element-232" data-mp="4hn0019kx362">
              {signalLoop.map((step, i) => <React.Fragment key={step} data-id="element-233" data-mp="3vnphxnrvi2dr" data-mpi={i}>
                  <span className="rounded-full bg-paper px-4 py-2 text-[12px] text-navy ring-1 ring-navy/[0.12]" data-id="element-234" data-mp="3vr3j4sosw5jm">
                    {step}
                  </span>
                  {i < signalLoop.length - 1 && <ArrowRightIcon className="h-3.5 w-3.5 text-navy/40" aria-hidden="true" data-id="element-235" data-mp="9fg7cxmmc37x" />}
                </React.Fragment>)}
            </div>
            <p className="mt-8 flex items-center gap-3 font-display text-[24px] font-semibold leading-tight text-navy" data-id="element-236" data-mp="2b9xg2iy534u4">
              <span aria-hidden="true" className="h-[2px] w-8 rounded-full bg-mint" data-id="element-237" data-mp="30yuj03zq1cn3" />
              Learn what works. Do more of it.
            </p>
            <button type="button" onClick={onOpenSignals} className="mt-10 inline-flex items-center gap-2.5 rounded-full bg-navy px-6 py-3.5 text-[14px] font-medium text-paper shadow-[0_18px_40px_-20px_rgba(11,42,74,0.65)] transition-colors duration-150 hover:bg-navy2 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue focus-visible:ring-offset-2" data-id="element-238" data-mp="39xzjhfuufg1w">
              What we learn
            </button>
          </InView>
        </div>

        <InView delay={0.1} y={26} data-id="element-239" data-mp="1hmqhztms8e7">
          <figure className="rounded-[30px] bg-navy p-6 text-paper shadow-[0_50px_90px_-50px_rgba(11,42,74,0.8)] sm:p-8" data-id="element-240" data-mp="1gl7n96dihlx">
            <div className="flex items-center justify-between" data-id="element-241" data-mp="6ewf5d974g0z">
              <span className="text-[10px] uppercase tracking-[0.22em] text-mint" data-id="element-242" data-mp="bh5a2f5mps5q">Campaign intelligence</span>
              <span className="text-[10px] uppercase tracking-[0.16em] text-paper/40" data-id="element-243" data-mp="1vywdmxoy55wk">Illustrative</span>
            </div>

            <div className="mt-7 grid grid-cols-3 gap-3" data-id="element-244" data-mp="1r14izvwx35oo">
              {chapters.map(chapter => <div key={chapter.snak} className="rounded-[16px] bg-paper/[0.06] px-4 py-4" data-id="element-245" data-mp="1m21f4esob5ia">
                  <span className="block text-[9.5px] uppercase tracking-[0.14em] text-paper/45" data-id="element-246" data-mp="1h2yk7n6eqfo1">{chapter.label}</span>
                  <span className="mt-1 block font-display text-[16px] font-semibold leading-tight text-paper" data-id="element-247" data-mp="29pj98gzeufof">
                    {chapter.archetype}
                  </span>
                  <span className="mt-0.5 block text-[11px] text-paper/55" data-id="element-248" data-mp="24wq4ht3xz7cc">{chapter.verb}</span>
                </div>)}
            </div>

            <ol className="mt-8 border-t border-paper/10" data-id="element-249" data-mp="2xfdkwv7zty73">
              {measures.map((measure, i) => {
              const Icon = GLYPHS[measure.id];
              return <li key={measure.id} className="flex items-center gap-4 border-b border-paper/10 py-3.5 last:border-b-0" data-id="element-250" data-mp="2d6nh9heiix9y" data-mpi={i}>
                    <span className="w-5 text-[10px] tabular-nums text-paper/35" data-id="element-251" data-mp="2i6i85kyafmv0">{String(i + 1).padStart(2, '0')}</span>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-paper/[0.07] text-mint" data-id="element-252" data-mp="239r8rbom6b5e">
                      <Icon className="h-4 w-4" aria-hidden="true" data-id="element-253" data-mp="28943sjpn2oj2" />
                    </span>
                    <span className="text-[13.5px] leading-snug text-paper/85" data-id="element-254" data-mp="2vz7w3uxdn1ur">{measure.question}</span>
                  </li>;
            })}
            </ol>
            <figcaption className="mt-4 text-[11px] leading-relaxed text-paper/45" data-id="element-255" data-mp="2m6ixw1qu7khd">
              Signals inform the next campaign and improve activation.
            </figcaption>
          </figure>
        </InView>
      </div>
    </Slide>;
}